/**
 * AI Consumption Console (P3). /admin/ai-usage — gated ops.metrics.read at the
 * route; the budget config form is additionally gated on ops.app_config.write
 * (canWrite). --g-* tokens + @garden/ui Card/Skeleton; charts via Recharts.
 * Loading/empty/error states drafted consistent-with-system (DESIGN-SYSTEM
 * "missing a state" rule) — flagged in the PR as admin-undesigned territory.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Card, Skeleton } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useMe } from "@/features/settings/api";
import {
  useBudget, useByFeature, useByModel, useByUser, useToday, useTrend,
} from "@/features/admin/ai-usage/hooks";
import { SpendTrendChart } from "@/features/admin/ai-usage/components/SpendTrendChart";
import { BreakdownBarChart } from "@/features/admin/ai-usage/components/BreakdownBarChart";
import { BudgetGauge } from "@/features/admin/ai-usage/components/BudgetGauge";
import { TodaySpendGauge } from "@/features/admin/ai-usage/components/TodaySpendGauge";
import { BudgetAlertBanner } from "@/features/admin/ai-usage/components/BudgetAlertBanner";
import { BudgetConfigForm } from "@/features/admin/ai-usage/components/BudgetConfigForm";

const DAYS = 30;

export function AiUsagePage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data: profile } = useMe();
  const canWrite = (profile?.permissions ?? []).includes("ops.app_config.write");

  const today = useToday();
  const budget = useBudget();
  const trend = useTrend("daily", DAYS);
  const byFeature = useByFeature(DAYS);
  const byModel = useByModel(DAYS);
  const byUser = useByUser(DAYS, 20);

  return (
    <div data-testid="ai-usage-page" className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{t("aiUsage.title")}</h1>
        <p className="text-sm text-[var(--g-secondary)]">{t("aiUsage.subtitle")}</p>
      </header>

      {budget.data ? <BudgetAlertBanner budget={budget.data} /> : null}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {today.data ? <TodaySpendGauge today={today.data} /> : <Skeleton className="h-20" />}
        {budget.data ? (
          <>
            <Card className="p-4"><BudgetGauge status={budget.data.daily} label={t("aiUsage.gauge.daily")} /></Card>
            <Card className="p-4"><BudgetGauge status={budget.data.monthly} label={t("aiUsage.gauge.monthly")} /></Card>
          </>
        ) : (
          <><Skeleton className="h-20" /><Skeleton className="h-20" /></>
        )}
      </section>

      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t("aiUsage.trend.title")}</h2>
        {trend.isLoading ? <Skeleton className="h-64" />
          : trend.isError ? <p role="alert" className="text-sm text-[var(--g-danger)]">{t("aiUsage.error")}</p>
          : trend.data && trend.data.points.length > 0 ? <SpendTrendChart points={trend.data.points} label={t("aiUsage.trend.title")} />
          : <p className="text-sm text-[var(--g-secondary)]">{t("aiUsage.empty")}</p>}
      </Card>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="mb-2 text-sm font-semibold text-foreground">{t("aiUsage.byFeature.title")}</h2>
          {byFeature.data && byFeature.data.items.length > 0 ? <BreakdownBarChart items={byFeature.data.items} label={t("aiUsage.byFeature.title")} />
            : <p className="text-sm text-[var(--g-secondary)]">{t("aiUsage.empty")}</p>}
        </Card>
        <Card className="p-4">
          <h2 className="mb-2 text-sm font-semibold text-foreground">{t("aiUsage.byModel.title")}</h2>
          {byModel.data && byModel.data.items.length > 0 ? <BreakdownBarChart items={byModel.data.items} label={t("aiUsage.byModel.title")} />
            : <p className="text-sm text-[var(--g-secondary)]">{t("aiUsage.empty")}</p>}
        </Card>
      </section>

      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t("aiUsage.byUser.title")}</h2>
        {byUser.data && byUser.data.items.length > 0 ? (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[var(--g-secondary)]">
              <th className="py-1">{t("aiUsage.byUser.user")}</th>
              <th className="py-1">{t("aiUsage.byUser.kgs")}</th>
              <th className="py-1">{t("aiUsage.byUser.tokens")}</th>
            </tr></thead>
            <tbody>
              {byUser.data.items.map((u) => (
                <tr key={u.user_id} className="border-t border-[var(--g-hairline)]">
                  <td className="py-1 font-mono text-xs">{u.user_id.slice(0, 8)}</td>
                  <td className="py-1">{u.cost_kgs.toFixed(2)}</td>
                  <td className="py-1">{u.input_tokens + u.output_tokens}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-sm text-[var(--g-secondary)]">{t("aiUsage.empty")}</p>}
      </Card>

      <Card className="p-4">
        <h2 className="mb-2 text-sm font-semibold text-foreground">{t("aiUsage.budget.title")}</h2>
        {budget.data ? <BudgetConfigForm budget={budget.data} canWrite={canWrite} /> : <Skeleton className="h-40" />}
      </Card>
    </div>
  );
}
export default AiUsagePage;
